import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import { Classe } from '../../types';
import type { FieldChangeEvent } from '../../types/forms';

interface ClasseFormProps {
  classe?: Classe | null;
  onSubmit: (data: Omit<Classe, 'id'>) => void;
  onCancel: () => void;
}

const ClasseForm: React.FC<ClasseFormProps> = ({ classe, onSubmit, onCancel }) => {

  const [formData, setFormData] = useState<Omit<Classe, 'id'>>({
    nom: '',
    description: '',
    niveau: '',
    code: '',
    ordre: 1,
    estActif: true,
    dateCreation: new Date().toISOString().split('T')[0],
    dateModification: new Date().toISOString().split('T')[0],
    statut: 'draft' // Par défaut en brouillon
  });

  useEffect(() => {
    if (classe) {
      setFormData({
        nom: classe.nom || '',
        description: classe.description || '',
        niveau: classe.niveau || '',
        code: classe.code || '',
        ordre: classe.ordre || 1,
        estActif: classe.estActif ?? true,
        dateCreation: classe.dateCreation || new Date().toISOString().split('T')[0],
        dateModification: new Date().toISOString().split('T')[0],
        statut: classe.statut || 'draft',
        datePublication: classe.datePublication,
        historiqueStatut: classe.historiqueStatut
      });
    }
  }, [classe]);

  const handleChange = (field: keyof Omit<Classe, 'id'>) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'checkbox'
      ? (event.target as HTMLInputElement).checked
      : event.target.value;

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSelectChange = (field: keyof Omit<Classe, 'id'>) => (
    event: FieldChangeEvent
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Informations de base */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Informations de base</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nom de la classe"
                  value={formData.nom}
                  onChange={handleChange('nom')}
                  required
                  placeholder="ex: Troisième A, Terminale S1"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Niveau"
                  value={formData.niveau}
                  onChange={handleChange('niveau')}
                  required
                  placeholder="ex: 3ème, Terminale"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Code"
                  value={formData.code}
                  onChange={handleChange('code')}
                  placeholder="ex: 3A, TS1"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                  multiline
                  rows={3}
                  placeholder="Description de la classe (optionnel)"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Ordre d'affichage"
                  type="number"
                  value={formData.ordre}
                  onChange={handleChange('ordre')}
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Statut et activation */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Statut et activation</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.statut || 'draft'}
                    onChange={handleSelectChange('statut')}
                    required
                  >
                    <MenuItem value="draft">🔸 Brouillon</MenuItem>
                    <MenuItem value="published">✅ Publié</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.estActif}
                      onChange={(e) => setFormData(prev => ({ ...prev, estActif: e.target.checked }))}
                    />
                  }
                  label="Classe active"
                />
                <Typography variant="body2" color="textSecondary">
                  Une classe inactive ne sera pas visible dans l'application.
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" variant="contained">
          {classe ? 'Mettre à jour' : 'Créer'}
        </Button>
      </Box>
    </Box>
  );
};

export default ClasseForm;
